import { CONFIG, CONSUMER_TYPES, PRODUCER_TYPE_IDS, PRODUCER_TYPES } from "./config";
import type { SolveResult } from "./solver";
import { type GameState, dayIndexOf, phaseIndexOf } from "./state";

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

const barEl = el<HTMLDivElement>("hud-bar");
const angerEl = el<HTMLDivElement>("hud-anger");
const warningsEl = el<HTMLDivElement>("hud-warnings");
const gameOverOverlay = el<HTMLDivElement>("game-over-overlay");
const gameOverStats = el<HTMLDivElement>("game-over-stats");

function angerBar(label: string, value: number): string {
  const pct = Math.min(100, (value / CONFIG.angerGameOverThreshold) * 100);
  const danger = pct > 66 ? "danger" : pct > 33 ? "warn" : "ok";
  return `
    <div class="anger-row">
      <span class="anger-label">${label}</span>
      <div class="anger-track"><div class="anger-fill ${danger}" style="width:${pct}%"></div></div>
      <span class="anger-value">${Math.round(value)}/${CONFIG.angerGameOverThreshold}</span>
    </div>`;
}

function statsTable(record: Record<string, number>, labels: Record<string, { label: string }>): string {
  const entries = Object.entries(record).filter(([, v]) => v > 0);
  if (entries.length === 0) return "";
  return entries.map(([id, v]) => `<div>${labels[id]?.label ?? id}: ${Math.round(v)} MW</div>`).join("");
}

/** This phase's rated supply potential per producer type — dial and weather
 * for the current phase are already baked in (see solver.ts), so this moves
 * with the day phase and today's weather, same as the combined Grid figure
 * does. A type with nothing built just doesn't show up. */
function supplyByTypeText(byType: Record<string, number>): string {
  const parts = PRODUCER_TYPE_IDS.filter((id) => (byType[id] ?? 0) > 0.5).map(
    (id) => `${PRODUCER_TYPES[id].label}: ${Math.round(byType[id])} MW`,
  );
  return parts.length > 0 ? parts.join(" &nbsp; ") : "none built yet";
}

export function updateHud(state: GameState, preview: SolveResult): void {
  const day = dayIndexOf(state.turn, CONFIG.phasesPerDay);
  const phase = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
  const multiplier = Math.min(
    CONFIG.scoreMultiplierCap,
    1 + state.angerStreakDays * CONFIG.scoreMultiplierPerStreakDay,
  );

  barEl.innerHTML = `
    <div>
      <strong>Day ${day + 1}</strong> — ${CONFIG.phaseNames[phase]} &nbsp;
      Score: ${Math.round(state.score)} <span class="dim">(x${multiplier.toFixed(1)}, streak ${state.angerStreakDays}d)</span> &nbsp;
      Weather: <strong>${state.weatherToday.name}</strong> <span class="dim">→ ${state.weatherTomorrow.name} tomorrow</span>
    </div>
    <div class="dim">
      Lines: ${state.lineBudgetKm} km &nbsp; Control nodes: ${state.controlNodeBudget} &nbsp; Batteries: ${state.batteryBudget} &nbsp;
      Grid: ${Math.round(preview.totalSupplyPotentialMw)}/${Math.round(preview.totalDemandPotentialMw)} MW supply/demand
    </div>
    <div class="dim">
      Supply by type: ${supplyByTypeText(preview.supplyPotentialByType)}
    </div>
  `;

  angerEl.innerHTML = angerBar("Imbalance", state.imbalanceAnger) + angerBar("Unsatisfied", state.unsatisfiedAnger);

  const warnings: string[] = [];
  if (preview.saturatedLineIds.length > 0) {
    warnings.push(`⚠ ${preview.saturatedLineIds.length} line(s) will break next turn`);
  }
  // One line per affected island rather than one lumped total, and
  // explicit about which direction the mismatch runs — "mismatched" alone
  // didn't say whether an island was drowning in surplus or short.
  for (const imbalance of preview.componentImbalances) {
    if (imbalance.mismatchMw <= 0.5) continue;
    const mw = Math.round(imbalance.mismatchMw);
    if (imbalance.hasProducer) {
      const verb = imbalance.direction === "oversupply" ? "Oversupplied" : "Undersupplied";
      warnings.push(`${verb} by ${mw} MW on one island (imbalance anger)`);
    } else {
      warnings.push(`${mw} MW unsatisfied on an island with no producer (unsatisfied anger)`);
    }
  }
  warningsEl.innerHTML = warnings.map((w) => `<div class="warning">${w}</div>`).join("");

  if (state.gameOver) {
    gameOverOverlay.classList.remove("hidden");
    gameOverStats.innerHTML = `
      <div>Final score: <strong>${Math.round(state.score)}</strong></div>
      <div>Days survived: ${day + 1}</div>
      <div>Total MW served: ${Math.round(state.stats.totalMwServed)}</div>
      <div>Imbalance anger generated: ${Math.round(state.stats.totalImbalanceAngerGenerated)}</div>
      <div>Unsatisfied anger generated: ${Math.round(state.stats.totalUnsatisfiedAngerGenerated)}</div>
      <div>Lines built: ${state.stats.linesBuilt} &nbsp; dismantled: ${state.stats.linesDismantledByPlayer} &nbsp; broken: ${state.stats.linesBrokenBySaturation}</div>
      <div>Control nodes built: ${state.stats.controlNodesBuilt} &nbsp; Batteries built: ${state.stats.batteriesBuilt}</div>
      <div class="dim" style="margin-top:8px">MW served by producer type</div>
      ${statsTable(state.stats.mwServedByProducerType, PRODUCER_TYPES)}
      <div class="dim" style="margin-top:8px">MW served by consumer type</div>
      ${statsTable(state.stats.mwServedByConsumerType, CONSUMER_TYPES)}
    `;
  } else {
    gameOverOverlay.classList.add("hidden");
  }
}
